import { BulkUpdateSaleLotModalProps } from "components/BulkUpdateOptionalFieldsModal/types";
import React from "react";
import { BulkUpdateOptionalFieldsModal } from "components/BulkUpdateOptionalFieldsModal/Modal";
import { bulkUpdateSaleLotsActionCreator } from "actions";
import {
  baseLivestockFields,
  buyerFields,
  cattleAdvancedDraftingFields,
  commonAdvancedDraftingFields,
  dentitionFields,
  EIDExemptionFields,
  estimatedLiveWeightFields,
  sheepAdvancedDraftingFields,
  sheepDraftingAttributes,
  sheepJoiningFields,
  weightRangeAndDressingFields,
  transporterFields,
} from "components/BulkUpdateOptionalFieldsModal/SaleLots/Fields";
import { validationSchema } from "components/BulkUpdateOptionalFieldsModal/SaleLots/ValidationSchema";
import { Species } from "constants/species";
import { useSelector } from "react-redux";
import {
  getContextByModalType,
  getCurrentSale,
  getSaleLots,
  getSetting,
  selectAreWeightAndDressingFeaturesEnabled,
  selectDeploymentIdBySaleLotIdLookup,
  selectIsSaleSubTypeDentitionEnabled,
} from "selectors";
import {
  useHasSaleyardOrDeploymentPermission,
  useHasSaleyardPermission,
} from "hooks";
import {
  DeploymentPermissions,
  SaleyardPermissions,
} from "constants/permissions";
import { ModalTypes } from "constants/navigation";
import { ModelNames } from "components/BulkUpdateOptionalFieldsModal/util";

// "Configures" relevant fields for update - this can/will/should be extended to
// - include different fields for different sale (sub) types.
// - hoist (rootFieldOrder) some fields for some users.
// - permission hide/show relevent fields.
function useGetBulkUpdateFields() {
  // Drive what's shown based on the sale species.
  const sale = useSelector(getCurrentSale);
  const isSheepSale = sale.species_id === Species.SHEEP;
  const isWeightRangeAndDressingEnabled = useSelector(
    selectAreWeightAndDressingFeaturesEnabled,
  );

  const isDentitionEnabled = useSelector(selectIsSaleSubTypeDentitionEnabled);

  const hasDentitionPermission = useHasSaleyardOrDeploymentPermission(
    DeploymentPermissions.featureDentition,
    SaleyardPermissions.featureDentition,
  );

  const hasWeightRangePermission = useHasSaleyardOrDeploymentPermission(
    DeploymentPermissions.featureWeightRange,
    SaleyardPermissions.featureWeightRange,
  );

  const showWeightRangeAndDressing =
    isWeightRangeAndDressingEnabled && hasWeightRangePermission;

  const showDentition = isDentitionEnabled && hasDentitionPermission;

  const hasTransporterPermission = useHasSaleyardPermission(
    SaleyardPermissions.featureTransporters,
  );

  if (isSheepSale) {
    return baseLivestockFields
      .concat(showWeightRangeAndDressing ? weightRangeAndDressingFields : [])
      .concat(showDentition ? dentitionFields : [])
      .concat(estimatedLiveWeightFields)
      .concat(sheepJoiningFields)
      .concat(EIDExemptionFields)
      .concat(sheepAdvancedDraftingFields)
      .concat(sheepDraftingAttributes)
      .concat(commonAdvancedDraftingFields)
      .concat(hasTransporterPermission ? transporterFields : [])
      .concat(buyerFields);
  }
  // (probably) a cattle sale.
  return baseLivestockFields
    .concat(showWeightRangeAndDressing ? weightRangeAndDressingFields : [])
    .concat(showDentition ? dentitionFields : [])
    .concat(estimatedLiveWeightFields)
    .concat(cattleAdvancedDraftingFields)
    .concat(commonAdvancedDraftingFields)
    .concat(hasTransporterPermission ? transporterFields : [])
    .concat(buyerFields);
}

export function BulkUpdateSaleLotsModal(
  props: BulkUpdateSaleLotModalProps,
): React.JSX.Element {
  const { onClose } = props;

  const { saleLotIds, storedSaleLotIdsSetting, aggridRef } =
    useSelector(getContextByModalType(ModalTypes.BulkUpdateSaleLot)) || {};

  const storedSaleLotIds =
    useSelector(getSetting(storedSaleLotIdsSetting)) || null;

  const modelIds = saleLotIds || storedSaleLotIds;
  const bulkUpdateFields = useGetBulkUpdateFields();

  const saleLots = useSelector(getSaleLots);
  const deploymentSaleIdLookup = useSelector(
    selectDeploymentIdBySaleLotIdLookup,
  );

  const deploymentSaleIds = new Set<number>();

  modelIds?.forEach(modelId => {
    if (saleLots[modelId]) {
      deploymentSaleIds.add(deploymentSaleIdLookup[modelId]);
    }
  });

  const productField = bulkUpdateFields.find(f => f.fieldName === "product_id");
  if (productField) {
    if (deploymentSaleIds.size > 1) {
      productField.disabled = true;
      productField.helpText =
        "Product bulk update not allowed for multiple agencies";
    } else {
      productField.disabled = false;
      productField.helpText = "";
    }
  }
  const deploymentId =
    deploymentSaleIds.size === 1
      ? deploymentSaleIds.values().next().value
      : null;

  return (
    <BulkUpdateOptionalFieldsModal
      onClose={onClose}
      modelIds={modelIds}
      modelName={ModelNames.SALE_LOT}
      bulkUpdateActionCreator={bulkUpdateSaleLotsActionCreator}
      fields={bulkUpdateFields}
      initialValues={{}}
      modelObjects={saleLots}
      validationSchema={validationSchema}
      aggridRef={aggridRef}
      deploymentId={deploymentId}
    />
  );
}
